import React, { useState, useEffect } from "react";
import {
  collection,
  query,
  orderBy,
  doc,
  getDocs,
  addDoc,
  deleteDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../utils/firebase";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip as RechartsTooltip,
} from "recharts";
import {
  Plus,
  Trash2,
  Edit,
  Save,
  X,
  ArrowRight,
  TrendingUp,
  TrendingDown,
  Coins,
  FileText,
} from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const BudgetTab = ({ residentData, organizationId, organizationData }) => {
  /* ────────────────────────────── STATE ────────────────────────────── */
  const [income, setIncome] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [savingsGoals, setSavingsGoals] = useState([]);
  const [budgetSummary, setBudgetSummary] = useState({
    totalIncome: 0,
    totalExpenses: 0,
    netBalance: 0,
    savingsProgress: 0,
  });

  const [activeSection, setActiveSection] = useState("overview");
  const [showAddIncomeModal, setShowAddIncomeModal] = useState(false);
  const [showAddExpenseModal, setShowAddExpenseModal] = useState(false);
  const [showAddSavingsGoalModal, setShowAddSavingsGoalModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);

  /* ────────────────────────────── FORMS ────────────────────────────── */
  const [newIncome, setNewIncome] = useState({
    source: "",
    amount: "",
    frequency: "monthly",
    nextPayDate: "",
  });
  const [editIncome, setEditIncome] = useState({
    id: "",
    source: "",
    amount: "",
    frequency: "monthly",
    nextPayDate: "",
  });

  const [newExpense, setNewExpense] = useState({
    category: "",
    description: "",
    amount: "",
    dueDate: "",
    isPaid: false,
    isRecurring: false,
    frequency: "monthly",
  });
  const [editExpense, setEditExpense] = useState({
    id: "",
    category: "",
    description: "",
    amount: "",
    dueDate: "",
    isPaid: false,
    isRecurring: false,
    frequency: "monthly",
  });

  const [newSavingsGoal, setNewSavingsGoal] = useState({
    description: "",
    targetAmount: "",
    currentAmount: "0",
    targetDate: "",
    priority: "medium",
  });
  const [editSavingsGoal, setEditSavingsGoal] = useState({
    id: "",
    description: "",
    targetAmount: "",
    currentAmount: "0",
    targetDate: "",
    priority: "medium",
  });

  const CHART_SIZE = { height: 210, radius: 65 };

  /* ─────────────────────────── FIRESTORE ─────────────────────────── */
  useEffect(() => {
    if (residentData && organizationId) fetchBudgetData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [residentData, organizationId]);

  useEffect(() => {
    calculateBudgetSummary();
  }, [income, expenses, savingsGoals]);

  /** ── Income ────────────────────────────────────────────────────────── */
  const handleAddIncome = async (e) => {
    e.preventDefault();
    try {
      const ref = collection(
        db,
        `organizations/${organizationId}/residents/${residentData.id}/income`
      );
      await addDoc(ref, {
        ...newIncome,
        amount: parseFloat(newIncome.amount || 0).toFixed(2),
        residentId: residentData.id,
        residentName: `${residentData.firstName} ${residentData.lastName}`,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setNewIncome({
        source: "",
        amount: "",
        frequency: "monthly",
        nextPayDate: "",
      });
      setShowAddIncomeModal(false);
      fetchBudgetData();
    } catch (err) {
      console.error("Error adding income:", err);
    }
  };

  const handleEditIncome = async (e) => {
    e.preventDefault();
    try {
      const ref = doc(
        db,
        `organizations/${organizationId}/residents/${residentData.id}/income`,
        editIncome.id
      );
      await updateDoc(ref, {
        source: editIncome.source,
        amount: parseFloat(editIncome.amount || 0).toFixed(2),
        frequency: editIncome.frequency,
        nextPayDate: editIncome.nextPayDate,
        updatedAt: serverTimestamp(),
      });
      setSelectedItem(null);
      fetchBudgetData();
    } catch (err) {
      console.error("Error updating income:", err);
    }
  };

  /** ── Expenses ──────────────────────────────────────────────────────── */
  const handleAddExpense = async (e) => {
    e.preventDefault();
    try {
      const ref = collection(
        db,
        `organizations/${organizationId}/residents/${residentData.id}/expenses`
      );
      await addDoc(ref, {
        ...newExpense,
        amount: parseFloat(newExpense.amount || 0).toFixed(2),
        residentId: residentData.id,
        residentName: `${residentData.firstName} ${residentData.lastName}`,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setNewExpense({
        category: "",
        description: "",
        amount: "",
        dueDate: "",
        isPaid: false,
        isRecurring: false,
        frequency: "monthly",
      });
      setShowAddExpenseModal(false);
      fetchBudgetData();
    } catch (err) {
      console.error("Error adding expense:", err);
    }
  };

  const handleEditExpense = async (e) => {
    e.preventDefault();
    try {
      const ref = doc(
        db,
        `organizations/${organizationId}/residents/${residentData.id}/expenses`,
        editExpense.id
      );
      await updateDoc(ref, {
        category: editExpense.category,
        description: editExpense.description,
        amount: parseFloat(editExpense.amount || 0).toFixed(2),
        dueDate: editExpense.dueDate,
        isPaid: editExpense.isPaid,
        isRecurring: editExpense.isRecurring,
        frequency: editExpense.frequency,
        updatedAt: serverTimestamp(),
      });
      setSelectedItem(null);
      fetchBudgetData();
    } catch (err) {
      console.error("Error updating expense:", err);
    }
  };

  /** ── Savings Goals ─────────────────────────────────────────────────── */
  const handleAddSavingsGoal = async (e) => {
    e.preventDefault();
    try {
      const ref = collection(
        db,
        `organizations/${organizationId}/residents/${residentData.id}/savingsGoals`
      );
      await addDoc(ref, {
        ...newSavingsGoal,
        targetAmount: parseFloat(newSavingsGoal.targetAmount || 0).toFixed(2),
        currentAmount: parseFloat(newSavingsGoal.currentAmount || 0).toFixed(2),
        residentId: residentData.id,
        residentName: `${residentData.firstName} ${residentData.lastName}`,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setNewSavingsGoal({
        description: "",
        targetAmount: "",
        currentAmount: "0",
        targetDate: "",
        priority: "medium",
      });
      setShowAddSavingsGoalModal(false);
      fetchBudgetData();
    } catch (err) {
      console.error("Error adding savings goal:", err);
    }
  };

  const handleEditSavingsGoal = async (e) => {
    e.preventDefault();
    try {
      const ref = doc(
        db,
        `organizations/${organizationId}/residents/${residentData.id}/savingsGoals`,
        editSavingsGoal.id
      );
      await updateDoc(ref, {
        description: editSavingsGoal.description,
        targetAmount: parseFloat(editSavingsGoal.targetAmount || 0).toFixed(2),
        currentAmount: parseFloat(editSavingsGoal.currentAmount || 0).toFixed(
          2
        ),
        targetDate: editSavingsGoal.targetDate,
        priority: editSavingsGoal.priority,
        updatedAt: serverTimestamp(),
      });
      setSelectedItem(null);
      fetchBudgetData();
    } catch (err) {
      console.error("Error updating savings goal:", err);
    }
  };

  /** ── Generic helpers ──────────────────────────────────────────────── */
  const handleDeleteItem = async (id, type) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;
    try {
      const col =
        type === "income"
          ? "income"
          : type === "expense"
          ? "expenses"
          : "savingsGoals";
      await deleteDoc(
        doc(
          db,
          `organizations/${organizationId}/residents/${residentData.id}/${col}`,
          id
        )
      );
      fetchBudgetData();
    } catch (err) {
      console.error(`Error deleting ${type}:`, err);
    }
  };

  const handleTogglePaid = async (expense) => {
    try {
      const ref = doc(
        db,
        `organizations/${organizationId}/residents/${residentData.id}/expenses`,
        expense.id
      );
      await updateDoc(ref, {
        isPaid: !expense.isPaid,
        updatedAt: serverTimestamp(),
      });
      fetchBudgetData();
    } catch (err) {
      console.error("Error toggling paid status:", err);
    }
  };

  /** ── PDF REPORT ───────────────────────────────────────────────────── */
  const handleGenerateReport = () => {
    const docPDF = new jsPDF();

    /* Header */
    docPDF.setFontSize(20);
    docPDF.setFont(undefined, "bold");
    docPDF.text("Financial Budget Report", 105, 20, { align: "center" });
    docPDF.setFontSize(12);
    docPDF.setFont(undefined, "normal");
    docPDF.text(
      `For: ${residentData.firstName} ${residentData.lastName}`,
      105,
      30,
      { align: "center" }
    );
    docPDF.text(`Generated on: ${new Date().toLocaleDateString()}`, 105, 37, {
      align: "center",
    });

    /* Summary */
    docPDF.setFontSize(16);
    docPDF.setFont(undefined, "bold");
    docPDF.text("Budget Summary", 14, 50);
    docPDF.setFontSize(12);
    docPDF.setFont(undefined, "normal");
    docPDF.text(
      `Monthly Income: $${budgetSummary.totalIncome.toFixed(2)}`,
      14,
      60
    );
    docPDF.text(
      `Monthly Expenses: $${budgetSummary.totalExpenses.toFixed(2)}`,
      14,
      67
    );
    docPDF.text(`Net Balance: $${budgetSummary.netBalance.toFixed(2)}`, 14, 74);
    docPDF.text(
      `Savings Progress: ${budgetSummary.savingsProgress.toFixed(1)}%`,
      14,
      81
    );

    /* Income table */
    docPDF.setFontSize(16);
    docPDF.setFont(undefined, "bold");
    docPDF.text("Income Sources", 14, 95);
    const incomeTable = income.map((i) => [
      i.source,
      i.frequency,
      `$${parseFloat(i.amount).toFixed(2)}`,
      i.nextPayDate || "N/A",
    ]);
    autoTable(docPDF, {
      startY: 100,
      head: [["Source", "Frequency", "Amount", "Next Pay Date"]],
      body: incomeTable,
      theme: "grid",
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      styles: { fontSize: 10 },
    });

    /* Expenses table */
    const expY = docPDF.lastAutoTable.finalY + 15;
    docPDF.setFontSize(16);
    docPDF.setFont(undefined, "bold");
    docPDF.text("Expenses", 14, expY);
    const expenseTable = expenses.map((e) => [
      e.category,
      e.description,
      `$${parseFloat(e.amount).toFixed(2)}`,
      e.isRecurring ? e.frequency : "One-time",
      e.dueDate || "N/A",
      e.isPaid ? "Paid" : "Unpaid",
    ]);
    autoTable(docPDF, {
      startY: expY + 5,
      head: [
        [
          "Category",
          "Description",
          "Amount",
          "Frequency",
          "Due Date",
          "Status",
        ],
      ],
      body: expenseTable,
      theme: "grid",
      headStyles: { fillColor: [192, 57, 43], textColor: 255 },
      styles: { fontSize: 10 },
    });

    /* Savings table */
    const savY = docPDF.lastAutoTable.finalY + 15;
    docPDF.setFontSize(16);
    docPDF.setFont(undefined, "bold");
    docPDF.text("Savings Goals", 14, savY);
    const savingsTable = savingsGoals.map((g) => [
      g.description,
      `$${parseFloat(g.currentAmount).toFixed(2)}`,
      `$${parseFloat(g.targetAmount).toFixed(2)}`,
      g.targetAmount > 0
        ? (
            (parseFloat(g.currentAmount) / parseFloat(g.targetAmount)) *
            100
          ).toFixed(1) + "%"
        : "0%",
      g.targetDate || "N/A",
      g.priority,
    ]);
    autoTable(docPDF, {
      startY: savY + 5,
      head: [
        [
          "Description",
          "Current",
          "Target",
          "Progress",
          "Target Date",
          "Priority",
        ],
      ],
      body: savingsTable,
      theme: "grid",
      headStyles: { fillColor: [39, 174, 96], textColor: 255 },
      styles: { fontSize: 10 },
    });

    docPDF.save(
      `Budget_Report_${residentData.lastName}_${new Date()
        .toISOString()
        .slice(0, 10)}.pdf`
    );
  };

  const fetchBudgetData = async () => {
    setIsLoading(true);
    try {
      /* Income */
      const incomeSnap = await getDocs(
        query(
          collection(
            db,
            `organizations/${organizationId}/residents/${residentData.id}/income`
          ),
          orderBy("createdAt", "desc")
        )
      );
      setIncome(incomeSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

      /* Expenses */
      const expSnap = await getDocs(
        query(
          collection(
            db,
            `organizations/${organizationId}/residents/${residentData.id}/expenses`
          ),
          orderBy("createdAt", "desc")
        )
      );
      setExpenses(expSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

      /* Savings */
      const saveSnap = await getDocs(
        query(
          collection(
            db,
            `organizations/${organizationId}/residents/${residentData.id}/savingsGoals`
          ),
          orderBy("createdAt", "desc")
        )
      );
      setSavingsGoals(saveSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Error fetching budget data:", err);
    } finally {
      setIsLoading(false);
    }
  };

  /* ──────────────────────── SUMMARY CALCULATION ─────────────────────── */
  const calculateBudgetSummary = () => {
    const monthlyized = (amt, freq) => {
      let m = parseFloat(amt) || 0;
      if (freq === "weekly") m *= 4.33;
      else if (freq === "biweekly") m *= 2.17;
      else if (freq === "yearly") m /= 12;
      return m;
    };

    const totalIncome = income.reduce(
      (sum, i) => sum + monthlyized(i.amount, i.frequency),
      0
    );
    const totalExpenses = expenses.reduce(
      (sum, e) =>
        e.isRecurring ? sum + monthlyized(e.amount, e.frequency) : sum,
      0
    );
    const net = totalIncome - totalExpenses;

    const target = savingsGoals.reduce(
      (s, g) => s + (parseFloat(g.targetAmount) || 0),
      0
    );
    const current = savingsGoals.reduce(
      (s, g) => s + (parseFloat(g.currentAmount) || 0),
      0
    );

    setBudgetSummary({
      totalIncome,
      totalExpenses,
      netBalance: net,
      savingsProgress: target ? (current / target) * 100 : 0,
    });
  };

  /* ───────────────────────────── HELPERS ───────────────────────────── */

  /* Income pie chart */
  const renderIncomePieChart = () => {
    const bySource = {};
    income.forEach((i) => {
      const key = i.source || "Other";
      const amt =
        i.frequency === "weekly"
          ? parseFloat(i.amount) * 4.33
          : i.frequency === "biweekly"
          ? parseFloat(i.amount) * 2.17
          : i.frequency === "yearly"
          ? parseFloat(i.amount) / 12
          : parseFloat(i.amount);
      bySource[key] = (bySource[key] || 0) + amt;
    });

    const data = Object.entries(bySource).map(([name, value]) => ({
      name,
      value,
    }));
    if (!data.length)
      return (
        <p className="text-center text-slate-400">No income sources yet</p>
      );

    const COLORS = ["#60A5FA", "#FDBA74", "#34D399", "#FCD34D", "#A78BFA"];

    return (
      <ResponsiveContainer width="100%" height={CHART_SIZE.height}>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            outerRadius={CHART_SIZE.radius}
            label
          >
            {data.map((_, idx) => (
              <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
            ))}
          </Pie>
          <Legend
            layout="horizontal"
            verticalAlign="bottom"
            align="center"
            iconSize={10}
            wrapperStyle={{ fontSize: "0.75rem" }} // smaller legend text
          />
          <RechartsTooltip />
        </PieChart>
      </ResponsiveContainer>
    );
  };

  /* Expense pie chart (same as before) */
  const renderExpensePieChart = () => {
    const cat = {};
    expenses.forEach((e) => {
      if (!e.isRecurring) return;
      const key = e.category || "Other";
      const amt =
        e.frequency === "weekly"
          ? parseFloat(e.amount) * 4.33
          : e.frequency === "biweekly"
          ? parseFloat(e.amount) * 2.17
          : e.frequency === "yearly"
          ? parseFloat(e.amount) / 12
          : parseFloat(e.amount);
      cat[key] = (cat[key] || 0) + amt;
    });

    const data = Object.entries(cat).map(([name, value]) => ({ name, value }));
    if (!data.length)
      return (
        <p className="text-center text-slate-400">
          No recurring expenses to display
        </p>
      );

    const COLORS = [
      "#0088FE",
      "#00C49F",
      "#FFBB28",
      "#FF8042",
      "#A64AEE",
      "#FF6B6B",
    ];

    return (
      <ResponsiveContainer width="100%" height={CHART_SIZE.height}>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            outerRadius={CHART_SIZE.radius}
            label
          >
            {data.map((_, idx) => (
              <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
            ))}
          </Pie>
          <Legend
            layout="horizontal"
            verticalAlign="bottom"
            align="center"
            iconSize={10}
            wrapperStyle={{ fontSize: "0.75rem" }}
          />
          <RechartsTooltip />
        </PieChart>
      </ResponsiveContainer>
    );
  };

  /* Upcoming bills (next 7 days, unpaid) */
  const today = new Date();
  const inSeven = new Date();
  inSeven.setDate(today.getDate() + 7);
  const upcomingBills = expenses.filter(
    (e) =>
      e.dueDate &&
      !e.isPaid &&
      new Date(e.dueDate) >= today &&
      new Date(e.dueDate) <= inSeven
  );

  /* Filter helper for expenses section */
  const getFilteredExpenses = () => {
    if (activeSection === "expenses-recurring")
      return expenses.filter((e) => e.isRecurring);
    if (activeSection === "expenses-onetime")
      return expenses.filter((e) => !e.isRecurring);
    if (activeSection === "expenses-unpaid")
      return expenses.filter((e) => !e.isPaid);
    return expenses;
  };

  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-3 md:p-6 w-full h-full">
      {/* Budget Overview Section */}
      {activeSection === "overview" && (
        <div className="space-y-6">
          {isLoading ? (
            <div className="text-center text-white">Loading...</div>
          ) : (
            <>
              {/* ───────────────── Budget Snapshot ───────────────── */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* ── Summary Card ── */}
                <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4 lg:col-span-1">
                  <h3 className="text-lg font-medium text-white mb-3">
                    Budget Summary
                  </h3>

                  <p className="text-slate-300 text-sm">
                    Monthly&nbsp;Income:&nbsp;
                    <span className="font-medium">
                      ${budgetSummary.totalIncome.toFixed(2)}
                    </span>
                  </p>
                  <p className="text-slate-300 text-sm">
                    Monthly&nbsp;Expenses:&nbsp;
                    <span className="font-medium">
                      ${budgetSummary.totalExpenses.toFixed(2)}
                    </span>
                  </p>
                  <p className="text-slate-300 text-sm">
                    Net&nbsp;Balance:&nbsp;
                    <span
                      className={`font-medium ${
                        budgetSummary.netBalance >= 0
                          ? "text-emerald-400"
                          : "text-red-400"
                      }`}
                    >
                      ${budgetSummary.netBalance.toFixed(2)}
                    </span>
                  </p>
                  <p className="text-slate-300 text-sm">
                    Savings&nbsp;Progress:&nbsp;
                    <span className="font-medium">
                      {budgetSummary.savingsProgress.toFixed(1)}%
                    </span>
                  </p>

                  {/* Gauge bar for Net Balance */}
                  <div className="mt-4 h-2 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${
                          budgetSummary.totalIncome > 0
                            ? Math.min(
                                (budgetSummary.netBalance /
                                  budgetSummary.totalIncome) *
                                  100,
                                100
                              )
                            : 0
                        }%`,
                        backgroundColor:
                          budgetSummary.netBalance >= 0 ? "#10B981" : "#EF4444",
                      }}
                    />
                  </div>
                </div>

                {/* ── Income Pie ── */}
                <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
                  <h3 className="text-lg font-medium text-white mb-3">
                    Income Breakdown
                  </h3>
                  {renderIncomePieChart()}
                </div>

                {/* ── Expense Pie ── */}
                <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
                  <h3 className="text-lg font-medium text-white mb-3">
                    Expenses Breakdown
                  </h3>
                  {renderExpensePieChart()}
                </div>
              </div>

              {/* ───────────────── Savings Goals ───────────────── */}
              <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-white">
                    Savings Goals Progress
                  </h3>
                  <button
                    onClick={() => setShowAddSavingsGoalModal(true)}
                    className="p-1.5 bg-slate-700 hover:bg-slate-600 transition-colors rounded-lg text-white"
                  >
                    <Plus size={16} />
                  </button>
                </div>

                {/* Goals list */}
                {savingsGoals.length > 0 ? (
                  <div className="space-y-4">
                    {savingsGoals.map((goal) => {
                      const progress = Math.min(
                        (parseFloat(goal.currentAmount) /
                          parseFloat(goal.targetAmount || 1)) *
                          100,
                        100
                      );

                      return (
                        <div key={goal.id} className="space-y-2">
                          {/* title & controls */}
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="text-white text-base font-medium">
                                {goal.description}
                              </h4>
                              <p className="text-slate-400 text-xs">
                                ${parseFloat(goal.currentAmount).toFixed(2)} of
                                ${parseFloat(goal.targetAmount).toFixed(2)}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <p
                                className="text-sm font-medium"
                                style={{
                                  color:
                                    progress >= 100 ? "#10B981" : "#60A5FA",
                                }}
                              >
                                {progress.toFixed(1)}%
                              </p>
                              <button
                                onClick={() => {
                                  setSelectedItem({
                                    type: "savings",
                                    id: goal.id,
                                  });
                                  setEditSavingsGoal({
                                    id: goal.id,
                                    description: goal.description,
                                    targetAmount: goal.targetAmount,
                                    currentAmount: goal.currentAmount,
                                    targetDate: goal.targetDate || "",
                                    priority: goal.priority,
                                  });
                                }}
                                className="p-1.5 bg-slate-700 hover:bg-slate-600 transition-colors rounded-lg text-slate-300 hover:text-white"
                                title="Edit"
                              >
                                <Edit size={16} />
                              </button>
                              <button
                                onClick={() =>
                                  handleDeleteItem(goal.id, "savingsGoals")
                                }
                                className="p-1.5 bg-red-500/10 hover:bg-red-500/20 transition-colors rounded-lg text-red-400"
                                title="Delete"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>

                          {/* progress bar */}
                          <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full"
                              style={{
                                width: `${progress}%`,
                                backgroundColor:
                                  progress >= 100 ? "#10B981" : "#60A5FA",
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-[100px] bg-slate-800/30 rounded-lg">
                    <p className="text-slate-400 mb-3">No savings goals set</p>
                    <button
                      onClick={() => setShowAddSavingsGoalModal(true)}
                      className="px-3 py-2 bg-blue-500/70 hover:bg-blue-500 transition-colors rounded-lg text-white text-sm flex items-center gap-2"
                    >
                      <Plus size={16} />
                      Add Savings Goal
                    </button>
                  </div>
                )}
              </div>

              {/* ───────────────── Upcoming Bills ───────────────── */}
              <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
                <h3 className="text-lg font-medium text-white mb-3">
                  Upcoming Bills (next 7 days)
                </h3>
                {upcomingBills.length ? (
                  <ul className="space-y-2">
                    {upcomingBills.map((bill) => (
                      <li key={bill.id} className="flex justify-between">
                        <span className="text-slate-300 text-sm">
                          {bill.category} –{" "}
                          {new Date(bill.dueDate).toLocaleDateString()}
                        </span>
                        <span className="text-red-400 font-medium">
                          ${parseFloat(bill.amount).toFixed(2)}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-slate-400 text-sm">No bills due soon 🎉</p>
                )}
              </div>

              {/* ───────────────── Actions ───────────────── */}
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleGenerateReport}
                  className="px-4 py-2.5 bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 transition-colors rounded-lg flex items-center gap-2"
                >
                  <FileText size={18} />
                  Generate Report
                </button>
                <button
                  onClick={() => setActiveSection("income")}
                  className="px-4 py-2.5 bg-slate-700 hover:bg-slate-600 transition-colors rounded-lg text-white flex items-center gap-2"
                >
                  <TrendingUp size={18} />
                  Manage Income
                </button>
                <button
                  onClick={() => setActiveSection("expenses")}
                  className="px-4 py-2.5 bg-slate-700 hover:bg-slate-600 transition-colors rounded-lg text-white flex items-center gap-2"
                >
                  <TrendingDown size={18} />
                  Manage Expenses
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Income Section */}
      {activeSection === "income" && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-white">Income Sources</h2>
            <button
              onClick={() => setShowAddIncomeModal(true)}
              className="px-3 py-2 bg-blue-500/70 hover:bg-blue-500 transition-colors rounded-lg text-white text-sm flex items-center gap-2"
            >
              <Plus size={16} />
              Add Income
            </button>
          </div>
          <div className="space-y-3">
            {income.length > 0 ? (
              income.map((item) => (
                <div
                  key={item.id}
                  className="bg-slate-800/50 rounded-xl border border-slate-700 p-4"
                >
                  {selectedItem &&
                  selectedItem.type === "income" &&
                  selectedItem.id === item.id ? (
                    <form onSubmit={handleEditIncome} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-slate-400 text-sm mb-1">
                            Source
                          </label>
                          <input
                            type="text"
                            value={editIncome.source}
                            onChange={(e) =>
                              setEditIncome({
                                ...editIncome,
                                source: e.target.value,
                              })
                            }
                            className="w-full bg-slate-700 rounded-lg px-3 py-2 text-white border border-slate-600 focus:border-blue-500 transition-colors"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-slate-400 text-sm mb-1">
                            Amount ($)
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={editIncome.amount}
                            onChange={(e) =>
                              setEditIncome({
                                ...editIncome,
                                amount: e.target.value,
                              })
                            }
                            className="w-full bg-slate-700 rounded-lg px-3 py-2 text-white border border-slate-600 focus:border-blue-500 transition-colors"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-slate-400 text-sm mb-1">
                            Frequency
                          </label>
                          <select
                            value={editIncome.frequency}
                            onChange={(e) =>
                              setEditIncome({
                                ...editIncome,
                                frequency: e.target.value,
                              })
                            }
                            className="w-full bg-slate-700 rounded-lg px-3 py-2 text-white border border-slate-600 focus:border-blue-500 transition-colors"
                          >
                            <option value="weekly">Weekly</option>
                            <option value="biweekly">Bi-weekly</option>
                            <option value="monthly">Monthly</option>
                            <option value="yearly">Yearly</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-slate-400 text-sm mb-1">
                            Next Pay Date
                          </label>
                          <input
                            type="date"
                            value={editIncome.nextPayDate || ""}
                            onChange={(e) =>
                              setEditIncome({
                                ...editIncome,
                                nextPayDate: e.target.value,
                              })
                            }
                            className="w-full bg-slate-700 rounded-lg px-3 py-2 text-white border border-slate-600 focus:border-blue-500 transition-colors"
                          />
                        </div>
                      </div>
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setSelectedItem(null)}
                          className="px-3 py-2 bg-slate-700 hover:bg-slate-600 transition-colors rounded-lg text-white text-sm"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="px-3 py-2 bg-blue-500/70 hover:bg-blue-500 transition-colors rounded-lg text-white text-sm flex items-center gap-2"
                        >
                          <Save size={16} />
                          Save
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div>
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-lg font-medium text-white">
                            {item.source}
                          </h3>
                          <p className="text-sm text-slate-400 capitalize">
                            {item.frequency} income
                            {item.nextPayDate &&
                              ` • Next payment: ${new Date(
                                item.nextPayDate
                              ).toLocaleDateString()}`}
                          </p>
                        </div>
                        <div className="text-xl font-bold text-green-400">
                          ${parseFloat(item.amount).toFixed(2)}
                        </div>
                      </div>
                      <div className="flex justify-end gap-2 mt-4">
                        <button
                          onClick={() => {
                            setSelectedItem({ type: "income", id: item.id });
                            setEditIncome({
                              id: item.id,
                              source: item.source,
                              amount: item.amount,
                              frequency: item.frequency,
                              nextPayDate: item.nextPayDate || "",
                            });
                          }}
                          className="p-1.5 bg-slate-700 hover:bg-slate-600 transition-colors rounded-lg text-slate-300 hover:text-white"
                          title="Edit"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteItem(item.id, "income")}
                          className="p-1.5 bg-red-500/10 hover:bg-red-500/20 transition-colors rounded-lg text-red-400"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="bg-slate-800/30 p-8 rounded-xl text-center">
                <p className="text-slate-400 mb-4">
                  No income sources added yet
                </p>
                <button
                  onClick={() => setShowAddIncomeModal(true)}
                  className="px-4 py-2 bg-blue-500/70 hover:bg-blue-500 transition-colors rounded-lg text-white flex items-center gap-2 mx-auto"
                >
                  <Plus size={18} />
                  Add Your First Income Source
                </button>
              </div>
            )}
          </div>
          <div>
            <button
              onClick={() => setActiveSection("overview")}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 transition-colors rounded-lg text-white flex items-center gap-2"
            >
              <ArrowRight size={18} className="rotate-180" />
              Back to Overview
            </button>
          </div>
        </div>
      )}

      {/* Expenses Section */}
      {(activeSection === "expenses" ||
        activeSection === "expenses-recurring" ||
        activeSection === "expenses-onetime" ||
        activeSection === "expenses-unpaid") && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-white">Expenses</h2>
            <button
              onClick={() => setShowAddExpenseModal(true)}
              className="px-3 py-2 bg-blue-500/70 hover:bg-blue-500 transition-colors rounded-lg text-white text-sm flex items-center gap-2"
            >
              <Plus size={16} />
              Add Expense
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setActiveSection("expenses")}
              className={`px-3 py-1.5 rounded-lg text-sm ${
                activeSection === "expenses"
                  ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                  : "bg-slate-700 text-white"
              }`}
            >
              All
            </button>
            <button
              onClick={() => setActiveSection("expenses-recurring")}
              className={`px-3 py-1.5 rounded-lg text-sm ${
                activeSection === "expenses-recurring"
                  ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                  : "bg-slate-700 text-white"
              }`}
            >
              Recurring
            </button>
            <button
              onClick={() => setActiveSection("expenses-onetime")}
              className={`px-3 py-1.5 rounded-lg text-sm ${
                activeSection === "expenses-onetime"
                  ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                  : "bg-slate-700 text-white"
              }`}
            >
              One-time
            </button>
            <button
              onClick={() => setActiveSection("expenses-unpaid")}
              className={`px-3 py-1.5 rounded-lg text-sm ${
                activeSection === "expenses-unpaid"
                  ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                  : "bg-slate-700 text-white"
              }`}
            >
              Unpaid
            </button>
          </div>
          <div className="space-y-3">
            {getFilteredExpenses().length > 0 ? (
              getFilteredExpenses().map((expense) => (
                <div
                  key={expense.id}
                  className={`bg-slate-800/50 rounded-xl border border-slate-700 p-4 ${
                    expense.isPaid ? "opacity-70" : ""
                  }`}
                >
                  {selectedItem &&
                  selectedItem.type === "expense" &&
                  selectedItem.id === expense.id ? (
                    <form onSubmit={handleEditExpense} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-slate-400 text-sm mb-1">
                            Category
                          </label>
                          <input
                            type="text"
                            value={editExpense.category}
                            onChange={(e) =>
                              setEditExpense({
                                ...editExpense,
                                category: e.target.value,
                              })
                            }
                            className="w-full bg-slate-700 rounded-lg px-3 py-2 text-white border border-slate-600 focus:border-blue-500 transition-colors"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-slate-400 text-sm mb-1">
                            Description
                          </label>
                          <input
                            type="text"
                            value={editExpense.description}
                            onChange={(e) =>
                              setEditExpense({
                                ...editExpense,
                                description: e.target.value,
                              })
                            }
                            className="w-full bg-slate-700 rounded-lg px-3 py-2 text-white border border-slate-600 focus:border-blue-500 transition-colors"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-slate-400 text-sm mb-1">
                            Amount ($)
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={editExpense.amount}
                            onChange={(e) =>
                              setEditExpense({
                                ...editExpense,
                                amount: e.target.value,
                              })
                            }
                            className="w-full bg-slate-700 rounded-lg px-3 py-2 text-white border border-slate-600 focus:border-blue-500 transition-colors"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-slate-400 text-sm mb-1">
                            Due Date
                          </label>
                          <input
                            type="date"
                            value={editExpense.dueDate || ""}
                            onChange={(e) =>
                              setEditExpense({
                                ...editExpense,
                                dueDate: e.target.value,
                              })
                            }
                            className="w-full bg-slate-700 rounded-lg px-3 py-2 text-white border border-slate-600 focus:border-blue-500 transition-colors"
                          />
                        </div>
                        <div className="flex items-center gap-4">
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={editExpense.isRecurring}
                              onChange={(e) =>
                                setEditExpense({
                                  ...editExpense,
                                  isRecurring: e.target.checked,
                                })
                              }
                              className="rounded border-slate-500 text-blue-500 focus:ring-blue-500 h-4 w-4 bg-slate-700"
                            />
                            <span className="text-slate-300 text-sm">
                              Recurring
                            </span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={editExpense.isPaid}
                              onChange={(e) =>
                                setEditExpense({
                                  ...editExpense,
                                  isPaid: e.target.checked,
                                })
                              }
                              className="rounded border-slate-500 text-green-500 focus:ring-green-500 h-4 w-4 bg-slate-700"
                            />
                            <span className="text-slate-300 text-sm">Paid</span>
                          </label>
                        </div>
                        {editExpense.isRecurring && (
                          <div>
                            <label className="block text-slate-400 text-sm mb-1">
                              Frequency
                            </label>
                            <select
                              value={editExpense.frequency}
                              onChange={(e) =>
                                setEditExpense({
                                  ...editExpense,
                                  frequency: e.target.value,
                                })
                              }
                              className="w-full bg-slate-700 rounded-lg px-3 py-2 text-white border border-slate-600 focus:border-blue-500 transition-colors"
                            >
                              <option value="weekly">Weekly</option>
                              <option value="biweekly">Bi-weekly</option>
                              <option value="monthly">Monthly</option>
                              <option value="yearly">Yearly</option>
                            </select>
                          </div>
                        )}
                      </div>
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setSelectedItem(null)}
                          className="px-3 py-2 bg-slate-700 hover:bg-slate-600 transition-colors rounded-lg text-white text-sm"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="px-3 py-2 bg-blue-500/70 hover:bg-blue-500 transition-colors rounded-lg text-white text-sm flex items-center gap-2"
                        >
                          <Save size={16} />
                          Save
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div>
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg font-medium text-white">
                              {expense.category}
                            </h3>
                            {expense.isRecurring && (
                              <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full">
                                {expense.frequency}
                              </span>
                            )}
                            {expense.isPaid && (
                              <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                                Paid
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-400 mt-1">
                            {expense.description}
                          </p>
                          {expense.dueDate && (
                            <p className="text-xs text-slate-500 mt-1">
                              Due:{" "}
                              {new Date(expense.dueDate).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        <div className="text-xl font-bold text-red-400">
                          ${parseFloat(expense.amount).toFixed(2)}
                        </div>
                      </div>
                      <div className="flex justify-end gap-2 mt-4">
                        <button
                          onClick={() => handleTogglePaid(expense)}
                          className={`px-2 py-1 transition-colors rounded-lg text-sm flex items-center gap-1 ${
                            expense.isPaid
                              ? "bg-slate-700 hover:bg-slate-600 text-slate-300"
                              : "bg-green-500/20 hover:bg-green-500/30 text-green-400"
                          }`}
                        >
                          {expense.isPaid ? "Mark Unpaid" : "Mark Paid"}
                        </button>
                        <button
                          onClick={() => {
                            setSelectedItem({
                              type: "expense",
                              id: expense.id,
                            });
                            setEditExpense({
                              id: expense.id,
                              category: expense.category,
                              description: expense.description,
                              amount: expense.amount,
                              dueDate: expense.dueDate || "",
                              isPaid: expense.isPaid,
                              isRecurring: expense.isRecurring,
                              frequency: expense.frequency || "monthly",
                            });
                          }}
                          className="p-1.5 bg-slate-700 hover:bg-slate-600 transition-colors rounded-lg text-slate-300 hover:text-white"
                          title="Edit"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() =>
                            handleDeleteItem(expense.id, "expense")
                          }
                          className="p-1.5 bg-red-500/10 hover:bg-red-500/20 transition-colors rounded-lg text-red-400"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="bg-slate-800/30 p-8 rounded-xl text-center">
                <p className="text-slate-400 mb-4">No expenses added yet</p>
                <button
                  onClick={() => setShowAddExpenseModal(true)}
                  className="px-4 py-2 bg-blue-500/70 hover:bg-blue-500 transition-colors rounded-lg text-white flex items-center gap-2 mx-auto"
                >
                  <Plus size={18} />
                  Add Your First Expense
                </button>
              </div>
            )}
          </div>
          <div>
            <button
              onClick={() => setActiveSection("overview")}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 transition-colors rounded-lg text-white flex items-center gap-2"
            >
              <ArrowRight size={18} className="rotate-180" />
              Back to Overview
            </button>
          </div>
        </div>
      )}

      {/* Add Income Modal */}
      {showAddIncomeModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900/90 border border-slate-700 rounded-xl shadow-lg w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/10 rounded-lg">
                  <TrendingUp className="text-green-400" size={18} />
                </div>
                <h3 className="text-lg font-medium text-white">
                  Add Income Source
                </h3>
              </div>
              <button
                onClick={() => setShowAddIncomeModal(false)}
                className="p-1.5 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X className="text-slate-400 hover:text-slate-300" size={18} />
              </button>
            </div>
            <form onSubmit={handleAddIncome} className="p-4 space-y-4">
              <div>
                <label className="block text-slate-400 text-sm mb-1">
                  Income Source
                </label>
                <input
                  type="text"
                  placeholder="E.g., Job, Side Gig, Support"
                  value={newIncome.source}
                  onChange={(e) =>
                    setNewIncome({ ...newIncome, source: e.target.value })
                  }
                  className="w-full bg-slate-800/50 rounded-lg px-3 py-2 text-white border border-slate-600 focus:border-green-500 transition-colors"
                  required
                />
              </div>
              <div>
                <label className="block text-slate-400 text-sm mb-1">
                  Amount ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={newIncome.amount}
                  onChange={(e) =>
                    setNewIncome({ ...newIncome, amount: e.target.value })
                  }
                  className="w-full bg-slate-800/50 rounded-lg px-3 py-2 text-white border border-slate-600 focus:border-green-500 transition-colors"
                  required
                />
              </div>
              <div>
                <label className="block text-slate-400 text-sm mb-1">
                  Frequency
                </label>
                <select
                  value={newIncome.frequency}
                  onChange={(e) =>
                    setNewIncome({ ...newIncome, frequency: e.target.value })
                  }
                  className="w-full bg-slate-800/50 rounded-lg px-3 py-2 text-white border border-slate-600 focus:border-green-500 transition-colors"
                >
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Bi-weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
              <div>
                <label className="block text-slate-400 text-sm mb-1">
                  Next Pay Date (Optional)
                </label>
                <input
                  type="date"
                  value={newIncome.nextPayDate}
                  onChange={(e) =>
                    setNewIncome({ ...newIncome, nextPayDate: e.target.value })
                  }
                  className="w-full bg-slate-800/50 rounded-lg px-3 py-2 text-white border border-slate-600 focus:border-green-500 transition-colors"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddIncomeModal(false)}
                  className="px-3 py-2 bg-slate-700 hover:bg-slate-600 transition-colors rounded-lg text-white text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-3 py-2 bg-green-500/80 hover:bg-green-500 transition-colors rounded-lg text-white text-sm flex items-center gap-2"
                >
                  <Plus size={16} />
                  Add Income
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Expense Modal */}
      {showAddExpenseModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900/90 border border-slate-700 rounded-xl shadow-lg w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500/10 rounded-lg">
                  <TrendingDown className="text-red-400" size={18} />
                </div>
                <h3 className="text-lg font-medium text-white">Add Expense</h3>
              </div>
              <button
                onClick={() => setShowAddExpenseModal(false)}
                className="p-1.5 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X className="text-slate-400 hover:text-slate-300" size={18} />
              </button>
            </div>
            <form onSubmit={handleAddExpense} className="p-4 space-y-4">
              <div>
                <label className="block text-slate-400 text-sm mb-1">
                  Category
                </label>
                <input
                  type="text"
                  placeholder="E.g., Rent, Utilities, Food"
                  value={newExpense.category}
                  onChange={(e) =>
                    setNewExpense({ ...newExpense, category: e.target.value })
                  }
                  className="w-full bg-slate-800/50 rounded-lg px-3 py-2 text-white border border-slate-600 focus:border-red-500 transition-colors"
                  required
                />
              </div>
              <div>
                <label className="block text-slate-400 text-sm mb-1">
                  Description
                </label>
                <input
                  type="text"
                  placeholder="E.g., Monthly rent payment"
                  value={newExpense.description}
                  onChange={(e) =>
                    setNewExpense({
                      ...newExpense,
                      description: e.target.value,
                    })
                  }
                  className="w-full bg-slate-800/50 rounded-lg px-3 py-2 text-white border border-slate-600 focus:border-red-500 transition-colors"
                  required
                />
              </div>
              <div>
                <label className="block text-slate-400 text-sm mb-1">
                  Amount ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={newExpense.amount}
                  onChange={(e) =>
                    setNewExpense({ ...newExpense, amount: e.target.value })
                  }
                  className="w-full bg-slate-800/50 rounded-lg px-3 py-2 text-white border border-slate-600 focus:border-red-500 transition-colors"
                  required
                />
              </div>
              <div>
                <label className="block text-slate-400 text-sm mb-1">
                  Due Date (Optional)
                </label>
                <input
                  type="date"
                  value={newExpense.dueDate}
                  onChange={(e) =>
                    setNewExpense({ ...newExpense, dueDate: e.target.value })
                  }
                  className="w-full bg-slate-800/50 rounded-lg px-3 py-2 text-white border border-slate-600 focus:border-red-500 transition-colors"
                />
              </div>
              <div className="space-y-3">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newExpense.isRecurring}
                    onChange={(e) =>
                      setNewExpense({
                        ...newExpense,
                        isRecurring: e.target.checked,
                      })
                    }
                    className="rounded border-slate-500 text-blue-500 focus:ring-blue-500 h-4 w-4 bg-slate-700"
                  />
                  <span className="text-slate-300 text-sm">
                    Recurring Expense
                  </span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newExpense.isPaid}
                    onChange={(e) =>
                      setNewExpense({ ...newExpense, isPaid: e.target.checked })
                    }
                    className="rounded border-slate-500 text-green-500 focus:ring-green-500 h-4 w-4 bg-slate-700"
                  />
                  <span className="text-slate-300 text-sm">Already Paid</span>
                </label>
              </div>
              {newExpense.isRecurring && (
                <div>
                  <label className="block text-slate-400 text-sm mb-1">
                    Frequency
                  </label>
                  <select
                    value={newExpense.frequency}
                    onChange={(e) =>
                      setNewExpense({
                        ...newExpense,
                        frequency: e.target.value,
                      })
                    }
                    className="w-full bg-slate-800/50 rounded-lg px-3 py-2 text-white border border-slate-600 focus:border-red-500 transition-colors"
                  >
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Bi-weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddExpenseModal(false)}
                  className="px-3 py-2 bg-slate-700 hover:bg-slate-600 transition-colors rounded-lg text-white text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-3 py-2 bg-red-500/80 hover:bg-red-500 transition-colors rounded-lg text-white text-sm flex items-center gap-2"
                >
                  <Plus size={16} />
                  Add Expense
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Savings Goal Modal */}
      {showAddSavingsGoalModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900/90 border border-slate-700 rounded-xl shadow-lg w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/10 rounded-lg">
                  <Coins className="text-blue-400" size={18} />
                </div>
                <h3 className="text-lg font-medium text-white">
                  Add Savings Goal
                </h3>
              </div>
              <button
                onClick={() => setShowAddSavingsGoalModal(false)}
                className="p-1.5 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X className="text-slate-400 hover:text-slate-300" size={18} />
              </button>
            </div>
            <form onSubmit={handleAddSavingsGoal} className="p-4 space-y-4">
              <div>
                <label className="block text-slate-400 text-sm mb-1">
                  Goal Description
                </label>
                <input
                  type="text"
                  placeholder="E.g., Emergency Fund, Car, Vacation"
                  value={newSavingsGoal.description}
                  onChange={(e) =>
                    setNewSavingsGoal({
                      ...newSavingsGoal,
                      description: e.target.value,
                    })
                  }
                  className="w-full bg-slate-800/50 rounded-lg px-3 py-2 text-white border border-slate-600 focus:border-blue-500 transition-colors"
                  required
                />
              </div>
              <div>
                <label className="block text-slate-400 text-sm mb-1">
                  Target Amount ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={newSavingsGoal.targetAmount}
                  onChange={(e) =>
                    setNewSavingsGoal({
                      ...newSavingsGoal,
                      targetAmount: e.target.value,
                    })
                  }
                  className="w-full bg-slate-800/50 rounded-lg px-3 py-2 text-white border border-slate-600 focus:border-blue-500 transition-colors"
                  required
                />
              </div>
              <div>
                <label className="block text-slate-400 text-sm mb-1">
                  Current Amount ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={newSavingsGoal.currentAmount}
                  onChange={(e) =>
                    setNewSavingsGoal({
                      ...newSavingsGoal,
                      currentAmount: e.target.value,
                    })
                  }
                  className="w-full bg-slate-800/50 rounded-lg px-3 py-2 text-white border border-slate-600 focus:border-blue-500 transition-colors"
                  required
                />
              </div>
              <div>
                <label className="block text-slate-400 text-sm mb-1">
                  Target Date (Optional)
                </label>
                <input
                  type="date"
                  value={newSavingsGoal.targetDate}
                  onChange={(e) =>
                    setNewSavingsGoal({
                      ...newSavingsGoal,
                      targetDate: e.target.value,
                    })
                  }
                  className="w-full bg-slate-800/50 rounded-lg px-3 py-2 text-white border border-slate-600 focus:border-blue-500 transition-colors"
                />
              </div>
              <div>
                <label className="block text-slate-400 text-sm mb-1">
                  Priority
                </label>
                <select
                  value={newSavingsGoal.priority}
                  onChange={(e) =>
                    setNewSavingsGoal({
                      ...newSavingsGoal,
                      priority: e.target.value,
                    })
                  }
                  className="w-full bg-slate-800/50 rounded-lg px-3 py-2 text-white border border-slate-600 focus:border-blue-500 transition-colors"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddSavingsGoalModal(false)}
                  className="px-3 py-2 bg-slate-700 hover:bg-slate-600 transition-colors rounded-lg text-white text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-3 py-2 bg-blue-500/80 hover:bg-blue-500 transition-colors rounded-lg text-white text-sm flex items-center gap-2"
                >
                  <Plus size={16} />
                  Add Goal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Savings Goal Form */}
      {selectedItem && selectedItem.type === "savings" && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900/90 border border-slate-700 rounded-xl shadow-lg w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/10 rounded-lg">
                  <Coins className="text-blue-400" size={18} />
                </div>
                <h3 className="text-lg font-medium text-white">
                  Edit Savings Goal
                </h3>
              </div>
              <button
                onClick={() => setSelectedItem(null)}
                className="p-1.5 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X className="text-slate-400 hover:text-slate-300" size={18} />
              </button>
            </div>
            <form onSubmit={handleEditSavingsGoal} className="p-4 space-y-4">
              <div>
                <label className="block text-slate-400 text-sm mb-1">
                  Goal Description
                </label>
                <input
                  type="text"
                  value={editSavingsGoal.description}
                  onChange={(e) =>
                    setEditSavingsGoal({
                      ...editSavingsGoal,
                      description: e.target.value,
                    })
                  }
                  className="w-full bg-slate-800/50 rounded-lg px-3 py-2 text-white border border-slate-600 focus:border-blue-500 transition-colors"
                  required
                />
              </div>
              <div>
                <label className="block text-slate-400 text-sm mb-1">
                  Target Amount ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editSavingsGoal.targetAmount}
                  onChange={(e) =>
                    setEditSavingsGoal({
                      ...editSavingsGoal,
                      targetAmount: e.target.value,
                    })
                  }
                  className="w-full bg-slate-800/50 rounded-lg px-3 py-2 text-white border border-slate-600 focus:border-blue-500 transition-colors"
                  required
                />
              </div>
              <div>
                <label className="block text-slate-400 text-sm mb-1">
                  Current Amount ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editSavingsGoal.currentAmount}
                  onChange={(e) =>
                    setEditSavingsGoal({
                      ...editSavingsGoal,
                      currentAmount: e.target.value,
                    })
                  }
                  className="w-full bg-slate-800/50 rounded-lg px-3 py-2 text-white border border-slate-600 focus:border-blue-500 transition-colors"
                  required
                />
              </div>
              <div>
                <label className="block text-slate-400 text-sm mb-1">
                  Target Date (Optional)
                </label>
                <input
                  type="date"
                  value={editSavingsGoal.targetDate}
                  onChange={(e) =>
                    setEditSavingsGoal({
                      ...editSavingsGoal,
                      targetDate: e.target.value,
                    })
                  }
                  className="w-full bg-slate-800/50 rounded-lg px-3 py-2 text-white border border-slate-600 focus:border-blue-500 transition-colors"
                />
              </div>
              <div>
                <label className="block text-slate-400 text-sm mb-1">
                  Priority
                </label>
                <select
                  value={editSavingsGoal.priority}
                  onChange={(e) =>
                    setEditSavingsGoal({
                      ...editSavingsGoal,
                      priority: e.target.value,
                    })
                  }
                  className="w-full bg-slate-800/50 rounded-lg px-3 py-2 text-white border border-slate-600 focus:border-blue-500 transition-colors"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setSelectedItem(null)}
                  className="px-3 py-2 bg-slate-700 hover:bg-slate-600 transition-colors rounded-lg text-white text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-3 py-2 bg-blue-500/80 hover:bg-blue-500 transition-colors rounded-lg text-white text-sm flex items-center gap-2"
                >
                  <Save size={16} />
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BudgetTab;
